import { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { WeeklyTimeScheduler } from "weekly-time-scheduler";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  coerceScheduleValue,
  getSupportedTimeZones,
  PRESETS,
  PRESET_LABELS,
  type ScheduleConditionValue,
  type ScheduleWindow,
} from "@/lib/schedule";

interface ScheduleConditionEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  value: unknown;
  onChange: (value: ScheduleConditionValue) => void;
}

function TimeZoneCombobox({
  value,
  onChange,
}: {
  value: string;
  onChange: (timezone: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const timeZones = useMemo(() => getSupportedTimeZones(), []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[260px] justify-between font-normal"
        >
          <span className="truncate">{value}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="end">
        <Command>
          <CommandInput placeholder="Search timezone..." />
          <CommandList>
            <CommandEmpty>No timezone found.</CommandEmpty>
            <CommandGroup>
              {timeZones.map((tz) => (
                <CommandItem
                  key={tz}
                  value={tz}
                  onSelect={() => {
                    onChange(tz);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn("mr-2 h-4 w-4", tz === value ? "opacity-100" : "opacity-0")}
                  />
                  {tz}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function ScheduleConditionEditDialog({
  isOpen,
  onClose,
  value,
  onChange,
}: ScheduleConditionEditDialogProps) {
  // Draft state: edits are held locally and only committed on Done, so Cancel
  // genuinely discards. (Deliberately unlike SubjectConditionEditPanel, which
  // writes through on every keystroke and whose Cancel is therefore a no-op.)
  const [draft, setDraft] = useState<ScheduleConditionValue>(() => coerceScheduleValue(value));

  // Re-seed each time the dialog opens so a discarded edit never lingers.
  useEffect(() => {
    if (isOpen) setDraft(coerceScheduleValue(value));
  }, [isOpen, value]);

  const setWindows = (windows: ScheduleWindow[]) => setDraft((d) => ({ ...d, windows }));
  const isEmpty = draft.windows.length === 0;

  const handleDone = () => {
    onChange(draft);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-[1080px]">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4 pr-6">
            <div className="space-y-1">
              <DialogTitle>Edit Schedule</DialogTitle>
              <DialogDescription>
                Select the times an email must be received in for this condition to match.
                Drag across the grid to paint a block; click a block to remove it.
              </DialogDescription>
            </div>
            <div className="space-y-1">
              <span className="text-sm font-medium">Timezone</span>
              <TimeZoneCombobox
                value={draft.timezone}
                onChange={(timezone) => setDraft((d) => ({ ...d, timezone }))}
              />
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-wrap gap-2">
          {PRESET_LABELS.map(({ key, label }) => (
            <Button
              key={key}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setWindows(PRESETS[key])}
            >
              {label}
            </Button>
          ))}
          <Button type="button" variant="ghost" size="sm" onClick={() => setWindows([])}>
            Clear all
          </Button>
        </div>

        <div className="overflow-x-auto">
          <WeeklyTimeScheduler value={draft.windows} onChange={setWindows} />
        </div>

        <DialogFooter className="sm:justify-between">
          <span className="text-sm text-muted-foreground self-center">
            {isEmpty ? "Select at least one time block to continue." : ""}
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" onClick={handleDone} disabled={isEmpty}>
              Done
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
